/*
 * main.js — La Maison Eterna global theme behaviour
 * --------------------------------------------------
 * Plain, dependency-free, progressive-enhancement vanilla JS. Replaces the
 * framer-motion / React behaviour from eterna-main by toggling CSS classes
 * (`.is-open`, `.is-hidden`, `hidden`). All CSS transitions for these classes
 * already live in the compiled stylesheet (assets/base.css).
 *
 * Every querySelector is guarded — markup may be absent on a given page.
 * Behaviours, by section:
 *   1. Header hide-on-scroll-down / show-on-scroll-up (past ~200px)
 *   2. Desktop mega menu — hover open with ~200ms close intent delay + chevron
 *   3. Mobile slide-out drawer — open/close, backdrop, body-scroll lock
 *   4. Drawer accordions — single-open, chevron rotation
 *   5. Search overlay — open/focus/close
 *   6. Global Esc handler — closes whichever of {drawer, search} is open
 *   7. Cart count — fetch /cart.js on load + on `cart:updated`
 */
(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState !== 'loading') {
      fn();
    } else {
      document.addEventListener('DOMContentLoaded', fn);
    }
  }

  ready(function () {
    /* ----------------------------------------------------------------------
     * 1. Header hide-on-scroll-down / show-on-scroll-up
     *    Mirrors Header.tsx: hide when scrolling down past 200px, show on up.
     * -------------------------------------------------------------------- */
    (function initHeaderHeight() {
      var header = document.querySelector('[data-header]');
      if (!header) return;
      function setHeaderHeight() {
        document.documentElement.style.setProperty('--header-height', header.offsetHeight + 'px');
      }
      setHeaderHeight();
      window.addEventListener('resize', setHeaderHeight, { passive: true });
    })();

    (function initHeaderScroll() {
      var header = document.querySelector('[data-header]');
      if (!header) return;

      var naturalHeaderHeight = header.offsetHeight + 'px';
      var lastScrollY = window.scrollY || 0;
      var hidden = false;
      var ticking = false;
      var DELTA = 6; // ignore sub-6px jitter (iOS rubber-band emits tiny deltas)

      function maxScroll() {
        return Math.max(0, (document.documentElement.scrollHeight || 0) - window.innerHeight);
      }

      function update() {
        ticking = false;
        // Clamp to the real scrollable range so iOS overscroll/rubber-band
        // (negative past the top, beyond max at the bottom) can't flip the header.
        var y = Math.max(0, Math.min(window.scrollY || 0, maxScroll()));
        var delta = y - lastScrollY;
        lastScrollY = y;
        if (Math.abs(delta) < DELTA) return;

        var shouldHide = delta > 0 && y > 200;
        if (shouldHide === hidden) return; // only touch the DOM on a real state change
        hidden = shouldHide;
        header.classList.toggle('is-hidden', hidden);
        document.documentElement.style.setProperty('--header-height', hidden ? '0px' : naturalHeaderHeight);
      }

      window.addEventListener('scroll', function () {
        if (!ticking) { ticking = true; window.requestAnimationFrame(update); }
      }, { passive: true });
    })();

    /* ----------------------------------------------------------------------
     * 2. Desktop mega menu — hover open with ~200ms close intent delay
     *    Triggers: [data-menu-trigger="collections|categories"] (container div)
     *    Panels:   [data-menu-panel="collections|categories"]
     *    Open on mouseenter of trigger OR its panel; close ~200ms after the
     *    pointer has left both. Rotate the chevron svg inside the trigger.
     * -------------------------------------------------------------------- */
    (function initMegaMenu() {
      var triggers = document.querySelectorAll('[data-menu-trigger]');
      if (!triggers.length) return;

      var closeTimer = null;

      function chevronOf(trigger) {
        return trigger ? trigger.querySelector('svg') : null;
      }

      function openMenu(name, trigger, panel) {
        // Close any other open menu first.
        document.querySelectorAll('[data-menu-panel].is-open').forEach(function (p) {
          if (p !== panel) p.classList.remove('is-open');
        });
        document.querySelectorAll('[data-menu-trigger]').forEach(function (t) {
          if (t !== trigger) {
            t.classList.remove('is-open');
            var triggerBtn = t.querySelector('.nav-trigger');
            if (triggerBtn) triggerBtn.setAttribute('aria-expanded', 'false');
            var c = chevronOf(t);
            if (c) c.style.transform = '';
          }
        });

        if (panel) panel.classList.add('is-open');
        if (trigger) {
          trigger.classList.add('is-open');
          var triggerButton = trigger.querySelector('.nav-trigger');
          if (triggerButton) triggerButton.setAttribute('aria-expanded', 'true');
          var chevron = chevronOf(trigger);
          if (chevron) chevron.style.transform = 'rotate(180deg)';
        }
      }

      function closeMenu(trigger, panel) {
        if (panel) panel.classList.remove('is-open');
        if (trigger) {
          trigger.classList.remove('is-open');
          var triggerBtn = trigger.querySelector('.nav-trigger');
          if (triggerBtn) triggerBtn.setAttribute('aria-expanded', 'false');
          var chevron = chevronOf(trigger);
          if (chevron) chevron.style.transform = '';
        }
      }

      triggers.forEach(function (trigger) {
        var name = trigger.getAttribute('data-menu-trigger');
        var panel = document.querySelector('[data-menu-panel="' + name + '"]');

        function scheduleOpen() {
          if (closeTimer) {
            clearTimeout(closeTimer);
            closeTimer = null;
          }
          openMenu(name, trigger, panel);
        }

        function scheduleClose() {
          if (closeTimer) clearTimeout(closeTimer);
          closeTimer = setTimeout(function () {
            closeMenu(trigger, panel);
            closeTimer = null;
          }, 200);
        }

        trigger.addEventListener('mouseenter', scheduleOpen);
        trigger.addEventListener('mouseleave', scheduleClose);

        if (panel) {
          panel.addEventListener('mouseenter', scheduleOpen);
          panel.addEventListener('mouseleave', scheduleClose);
        }
      });
    })();

    /* ----------------------------------------------------------------------
     * 3. Mobile slide-out drawer
     *    Open:    [data-drawer-open]   Close: [data-drawer-close]
     *    Drawer:  [data-drawer]        Backdrop: [data-drawer-backdrop]
     *    Toggle `is-open` on drawer + backdrop together; lock body scroll.
     * -------------------------------------------------------------------- */
    var drawer = document.querySelector('[data-drawer]');
    var drawerBackdrop = document.querySelector('[data-drawer-backdrop]');

    function isDrawerOpen() {
      return drawer && drawer.classList.contains('is-open');
    }

    function setDrawerExpanded(state) {
      document.querySelectorAll('[data-drawer-open]').forEach(function (btn) {
        btn.setAttribute('aria-expanded', state ? 'true' : 'false');
      });
    }

    function openDrawer() {
      if (drawer) drawer.classList.add('is-open');
      if (drawerBackdrop) drawerBackdrop.classList.add('is-open');
      setDrawerExpanded(true);
      document.body.style.overflow = 'hidden';
    }

    function closeDrawer() {
      if (drawer) drawer.classList.remove('is-open');
      if (drawerBackdrop) drawerBackdrop.classList.remove('is-open');
      setDrawerExpanded(false);
      document.body.style.overflow = '';
    }

    (function initDrawer() {
      document.querySelectorAll('[data-drawer-open]').forEach(function (btn) {
        btn.addEventListener('click', openDrawer);
      });
      document.querySelectorAll('[data-drawer-close]').forEach(function (btn) {
        btn.addEventListener('click', closeDrawer);
      });
      if (drawerBackdrop) {
        drawerBackdrop.addEventListener('click', closeDrawer);
      }
    })();

    /* ----------------------------------------------------------------------
     * 4. Drawer accordions — single-open behaviour
     *    Each wrapped in [data-accordion] with a [data-accordion-toggle] button
     *    and a `.accordion-body`. Clicking a toggle flips `is-open` on its body;
     *    opening one closes its siblings. Rotate that toggle's chevron svg.
     * -------------------------------------------------------------------- */
    (function initAccordions() {
      var accordions = document.querySelectorAll('[data-accordion]');
      if (!accordions.length) return;

      function setChevron(toggle, open) {
        var chevron = toggle ? toggle.querySelector('svg') : null;
        if (chevron) chevron.style.transform = open ? 'rotate(180deg)' : '';
        if (toggle) toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      }

      accordions.forEach(function (accordion) {
        var toggle = accordion.querySelector('[data-accordion-toggle]');
        var body = accordion.querySelector('.accordion-body');
        if (!toggle || !body) return;

        toggle.addEventListener('click', function () {
          var willOpen = !body.classList.contains('is-open');

          // Single-open: close all siblings first.
          accordions.forEach(function (other) {
            if (other === accordion) return;
            var otherBody = other.querySelector('.accordion-body');
            var otherToggle = other.querySelector('[data-accordion-toggle]');
            if (otherBody) otherBody.classList.remove('is-open');
            setChevron(otherToggle, false);
          });

          body.classList.toggle('is-open', willOpen);
          setChevron(toggle, willOpen);
        });
      });
    })();

    /* ----------------------------------------------------------------------
     * 5. Search overlay
     *    Open: [data-search-open]   Close: [data-search-close]
     *    Overlay: [data-search-overlay]   Input: [data-search-input]
     *    Toggle `is-open`; focus input ~50ms after open; close on button/Esc.
     * -------------------------------------------------------------------- */
    var searchOverlay = document.querySelector('[data-search-overlay]');
    var searchInput = document.querySelector('[data-search-input]');

    function isSearchOpen() {
      return searchOverlay && searchOverlay.classList.contains('is-open');
    }

    function setSearchExpanded(state) {
      document.querySelectorAll('[data-search-open]').forEach(function (btn) {
        btn.setAttribute('aria-expanded', state ? 'true' : 'false');
      });
    }

    function openSearch() {
      if (!searchOverlay) return;
      searchOverlay.classList.add('is-open');
      setSearchExpanded(true);
      if (searchInput) {
        setTimeout(function () {
          searchInput.focus();
        }, 50);
      }
    }

    function closeSearch() {
      if (searchOverlay) searchOverlay.classList.remove('is-open');
      setSearchExpanded(false);
    }

    (function initSearch() {
      document.querySelectorAll('[data-search-open]').forEach(function (btn) {
        btn.addEventListener('click', openSearch);
      });
      document.querySelectorAll('[data-search-close]').forEach(function (btn) {
        btn.addEventListener('click', closeSearch);
      });
    })();

    /* ----------------------------------------------------------------------
     * 6. Global Esc handler — closes whichever overlay is open.
     * -------------------------------------------------------------------- */
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' && e.key !== 'Esc') return;
      if (isSearchOpen()) closeSearch();
      if (isDrawerOpen()) closeDrawer();
    });

    /* ----------------------------------------------------------------------
     * 7. Cart count — fetch /cart.js on load and on `cart:updated`.
     *    Updates [data-cart-count] textContent + toggles `hidden` when empty.
     * -------------------------------------------------------------------- */
    (function initCartCount() {
      var cartCountEl = document.querySelector('[data-cart-count]');
      if (!cartCountEl) return;

      function refreshCartCount() {
        try {
          fetch('/cart.js', { credentials: 'same-origin' })
            .then(function (res) {
              if (!res.ok) return null;
              return res.json();
            })
            .then(function (cart) {
              if (!cart) return;
              var count = cart.item_count || 0;
              cartCountEl.textContent = count;
              cartCountEl.classList.toggle('hidden', count === 0);
            })
            .catch(function () {
              /* no-op on failure */
            });
        } catch (err) {
          /* no-op on failure */
        }
      }

      // Optimistic bump (no fetch) on add; absolute set (no fetch) on reconcile.
      function bumpCartCount(by) {
        var cur = parseInt(cartCountEl.textContent, 10) || 0;
        var next = Math.max(0, cur + (by || 0));
        cartCountEl.textContent = next;
        cartCountEl.classList.toggle('hidden', next === 0);
      }
      function setCartCount(n) {
        if (typeof n !== 'number' || n < 0) { return; }
        cartCountEl.textContent = n;
        cartCountEl.classList.toggle('hidden', n === 0);
      }

      refreshCartCount();
      document.addEventListener('cart:updated', refreshCartCount);
      document.addEventListener('cart:added', function (e) {
        var q = (e && e.detail && e.detail.quantity) || 1;
        bumpCartCount(q);
      });
      document.addEventListener('cart:count', function (e) {
        if (e && e.detail && typeof e.detail.count === 'number') { setCartCount(e.detail.count); }
      });
    })();

    /* ----------------------------------------------------------------------
     * 7b. Add-to-cart feedback — toast + cart-icon bump.
     *     Listens for `cart:added` (dispatched only on ADD paths, not on
     *     qty-decrease/remove). detail: { title, image, quantity }.
     * -------------------------------------------------------------------- */
    (function initToastSystem() {
      var region = document.querySelector('[data-cart-toast-region]');
      var cartCountEl = document.querySelector('[data-cart-count]');
      var cartLink = cartCountEl ? cartCountEl.closest('a') : null;

      // Inline icons (ported from lucide-react usage in ToastContext.tsx).
      var CHECK = '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>';
      var ALERT = '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" x2="12" y1="8" y2="12"/><line x1="12" x2="12.01" y1="16" y2="16"/></svg>';
      var BAG = '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>';
      var X = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>';

      function iconFor(type) {
        if (type === 'success') return '<span class="cart-toast__icon cart-toast__icon--success">' + CHECK + '</span>';
        if (type === 'error') return '<span class="cart-toast__icon cart-toast__icon--error">' + ALERT + '</span>';
        if (type === 'cart') return '<span class="cart-toast__icon cart-toast__icon--cart">' + BAG + '</span>';
        return '<span class="cart-toast__dot"></span>';
      }

      function bumpCartIcon() {
        if (!cartLink) return;
        cartLink.classList.remove('cart-bump');
        // force reflow so the animation replays on rapid successive adds
        void cartLink.offsetWidth;
        cartLink.classList.add('cart-bump');
        setTimeout(function () { cartLink.classList.remove('cart-bump'); }, 600);
      }

      // showToast(message, type) — type ∈ success | error | cart | info (default info)
      function showToast(message, type) {
        if (!region || !message) return;
        type = type || 'info';

        var toast = document.createElement('div');
        toast.className = 'cart-toast';
        toast.setAttribute('role', 'status');
        toast.innerHTML =
          iconFor(type) +
          '<p class="cart-toast__msg"></p>' +
          '<button type="button" class="cart-toast__close" aria-label="Dismiss">' + X + '</button>';
        // assign text via textContent to avoid any HTML injection
        toast.querySelector('.cart-toast__msg').textContent = message;

        region.appendChild(toast);
        // animate in on next frame
        requestAnimationFrame(function () {
          requestAnimationFrame(function () { toast.classList.add('is-visible'); });
        });

        var dismiss = function () {
          toast.classList.remove('is-visible');
          setTimeout(function () { if (toast.parentNode) toast.parentNode.removeChild(toast); }, 350);
        };
        var t = setTimeout(dismiss, 2500);
        toast.querySelector('.cart-toast__close').addEventListener('click', function (e) {
          e.stopPropagation(); clearTimeout(t); dismiss();
        });
      }

      // Expose as a shared toast so any inline section script can reuse it.
      window.lmeToast = showToast;

      // Add-to-cart: brand message + cart-icon bump (matches ToastContext "success").
      document.addEventListener('cart:added', function () {
        bumpCartIcon();
        showToast('Added to your selection', 'success');
      });

      // Generic channel: detail { message, type } — used by wishlist/share/etc.
      document.addEventListener('lme:toast', function (e) {
        var d = (e && e.detail) || {};
        showToast(d.message, d.type);
      });
    })();

    /* ----------------------------------------------------------------------
     * 8. Wishlist count — reflects localStorage['wishlist'] (array of handles).
     *    Updates [data-wishlist-count] textContent + toggles `hidden` when empty.
     *    Refreshes on load, on the `wishlistUpdate` event (dispatched by the
     *    product card / product page / wishlist page), and on cross-tab `storage`.
     * -------------------------------------------------------------------- */
    (function initWishlistCount() {
      var wishCountEl = document.querySelector('[data-wishlist-count]');
      if (!wishCountEl) return;

      function refreshWishlistCount() {
        var count = 0;
        try {
          var arr = JSON.parse(localStorage.getItem('wishlist') || '[]');
          if (Array.isArray(arr)) count = arr.length;
        } catch (err) {
          /* malformed storage -> treat as empty */
        }
        wishCountEl.textContent = count;
        wishCountEl.classList.toggle('hidden', count === 0);
      }

      refreshWishlistCount();
      window.addEventListener('wishlistUpdate', refreshWishlistCount);
      window.addEventListener('storage', function (e) {
        if (e.key === 'wishlist') refreshWishlistCount();
      });
    })();

    /* ----------------------------------------------------------------------
     * 9. Card image load — stop the shimmer on a card's image container once
     *    its <img class="lme-img"> has loaded. Covers server-rendered grids
     *    and JS-appended cards (collection load-more, wishlist) via a
     *    MutationObserver. No opacity toggling → safe if JS never runs.
     * -------------------------------------------------------------------- */
    (function initCardImageLoad() {
      function clearShimmer(img) {
        var box = img.closest('.lme-skeleton');
        if (box) box.classList.remove('lme-skeleton');
      }
      function watch(img) {
        if (img.complete && img.naturalWidth > 0) { clearShimmer(img); return; }
        img.addEventListener('load', function () { clearShimmer(img); }, { once: true });
        img.addEventListener('error', function () { clearShimmer(img); }, { once: true });
      }
      function scan(node) {
        if (node.matches && node.matches('img.lme-img')) watch(node);
        if (node.querySelectorAll) node.querySelectorAll('img.lme-img').forEach(watch);
      }
      scan(document);
      var mo = new MutationObserver(function (muts) {
        muts.forEach(function (m) {
          Array.prototype.forEach.call(m.addedNodes, function (n) {
            if (n.nodeType === 1) scan(n);
          });
        });
      });
      mo.observe(document.body, { childList: true, subtree: true });
    })();
  });
})();
